import { useLocation } from 'react-router-dom'
import { Bell, Wifi, WifiOff, Menu } from 'lucide-react'
import { useSelector } from 'react-redux'
import { UserButton, useUser } from '@clerk/react'
import { useAuth } from '../../hooks/useAuth'
import EmergencyStop from '../common/EmergencyStop'

const PAGE_TITLES = {
  '/dashboard': 'Dashboard',
  '/exchanges': 'Exchange Connections',
  '/bots': 'Trading Bots',
  '/strategies': 'Strategy Library',
  '/backtesting': 'Backtesting Engine',
  '/trading': 'Live Trading',
  '/marketplace': 'Signal Marketplace',
  '/signals': 'My Signals',
  '/course': 'Algo Trading Course',
  '/reports': 'Performance Reports',
  '/subscription': 'Subscription',
  '/ai': 'AI Assistant',
  '/settings': 'Settings',
}

export default function Header({ onMenuClick }) {
  const { pathname } = useLocation()
  const { user: clerkUser } = useUser()
  const { triggerEmergencyStop } = useAuth()
  const connected = useSelector((s) => s.market.connected)
  const title = PAGE_TITLES[pathname] || 'TradeBotHub Pro'

  return (
    <header className="flex items-center justify-between px-3 sm:px-6 py-3 sm:py-4 border-b border-surface-200 bg-surface-50 shrink-0 gap-3">
      <div className="flex items-center gap-3 min-w-0">
        {/* Hamburger — mobile only */}
        <button
          onClick={onMenuClick}
          className="md:hidden p-1.5 text-gray-400 hover:text-white hover:bg-surface-100 rounded-lg transition-colors shrink-0"
          aria-label="Open menu"
        >
          <Menu size={20} />
        </button>

        <div className="min-w-0">
          <h1 className="text-sm sm:text-lg font-semibold text-white truncate">{title}</h1>
          <p className="text-xs text-gray-500 hidden sm:block">
            {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
        {/* WS Status */}
        <div className={`flex items-center gap-1 sm:gap-1.5 text-xs font-medium px-2 py-1 rounded-full ${connected ? 'text-trading-green bg-trading-green/10' : 'text-gray-500 bg-surface-100'}`}>
          {connected ? <Wifi size={11} /> : <WifiOff size={11} />}
          <span className="hidden sm:inline">{connected ? 'Live' : 'Offline'}</span>
        </div>

        {/* Emergency Stop — hidden on very small screens */}
        <div className="hidden xs:block sm:block">
          <EmergencyStop compact />
        </div>

        {/* Notifications */}
        <button className="relative p-1.5 sm:p-2 text-gray-400 hover:text-white hover:bg-surface-100 rounded-lg transition-colors">
          <Bell size={16} className="sm:w-[18px] sm:h-[18px]" />
          <span className="absolute top-1 right-1 sm:top-1.5 sm:right-1.5 w-1.5 h-1.5 bg-trading-red rounded-full" />
        </button>

        {/* User Menu */}
        <div className="flex items-center gap-2 pl-2 sm:pl-3 border-l border-surface-200">
          <div className="hidden sm:block text-right">
            <p className="text-sm font-medium text-white leading-tight">
              {clerkUser?.username || clerkUser?.firstName || clerkUser?.primaryEmailAddress?.emailAddress?.split('@')[0] || 'User'}
            </p>
          </div>
          <UserButton afterSignOutUrl="/login" />
        </div>
      </div>
    </header>
  )
}
